/**
 * Password input with a show/hide toggle (eye icon). Spreads all native input
 * props through, so it's a drop-in for `<input type="password" .../>`. Styling
 * lives in native-auth.css (.password-field / .password-toggle).
 */
import {InputHTMLAttributes, ReactNode, useEffect, useState} from 'react';

type PasswordFieldProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  /** Optional icon rendered inside the field on the left (e.g. a padlock). */
  leftIcon?: ReactNode;
  /** Bump this (e.g. after generating) to force the field to reveal its value. */
  revealNonce?: number;
};

const EyeIcon = (): JSX.Element => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon = (): JSX.Element => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

const PasswordField = ({leftIcon, revealNonce, ...props}: PasswordFieldProps): JSX.Element => {
  const [visible, setVisible] = useState(false);

  // Reveal the value whenever the parent bumps revealNonce (e.g. after generating
  // a password, so the user can read/save it). The initial 0 never reveals.
  useEffect(() => {
    if (revealNonce) {
      setVisible(true);
    }
  }, [revealNonce]);

  return (
    <div className={leftIcon ? 'password-field has-left-icon' : 'password-field'}>
      {leftIcon ? <span className="field-left-icon">{leftIcon}</span> : null}
      <input {...props} type={visible ? 'text' : 'password'} />
      <button
        type="button"
        className="password-toggle"
        onClick={() => setVisible((value) => !value)}
        aria-label={visible ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
        tabIndex={-1}
      >
        {visible ? <EyeOffIcon /> : <EyeIcon />}
      </button>
    </div>
  );
};

export default PasswordField;
